import { t } from "@web-monorepo/i18n/macro";
import { DDSDisplayText, DDSIconButton } from "@web-monorepo/dds";
import { IconChevronLeft } from "@web-monorepo/dds-icons";
import type { TranslatedString } from "@web-monorepo/translation-runtime";
import { clsx } from "clsx";
import React from "react";
import FixedHeaderScroller from "./FixedHeaderScroller";
import type { ToolkitActivityVariant } from "./activityVariant";
import { closeWebview } from "src/pods/webviewInit";
import styles from "./ToolkitAppScroller.module.css";

type ToolkitAppScrollerProps = {
  title: TranslatedString;
  // `variant` picks the activity's brand color out of `ToolkitAppScroller.module.css`.
  // See `./activityVariant.ts` for the full set + the recipe for adding a new one.
  variant: ToolkitActivityVariant;
  imgSrc: string;
  children: React.ReactNode;
};

export default function ToolkitAppScroller({ title, variant, imgSrc, children }: ToolkitAppScrollerProps) {
  // The variant class sets the `--activity-bg` / `--activity-bg-image` CSS
  // variables on the root; the header/content/hero rules read them. So the
  // bg propagates without any inline `style` at all.
  const variantClass = styles[variant];
  return (
    <FixedHeaderScroller
      headerClassName={clsx(variantClass, styles.headerBg)}
      contentClassName={clsx(variantClass, styles.contentOverscrollBg)}
      header={
        <div className={styles.header}>
          <DDSIconButton
            data-name="toolkit_app_scroller:back"
            aria-label={t`Go back`}
            tooltip={{ content: t`Go back`, dataName: "toolkit_app_scroller:back:tooltip" }}
            variant="transparent"
            className={styles.backButton}
            onClick={() => {
              // for iOS native toolkit landing page
              // handling on the iOS side for this action does not exist on older clients
              // so shouldn't affect toolkit with the webview landing page
              closeWebview();
              window.history.back();
            }}
          >
            <IconChevronLeft />
          </DDSIconButton>
          <div className={styles.titleRow}>
            <DDSDisplayText as="h1" level="sm" className={styles.title}>
              {title}
            </DDSDisplayText>
          </div>
        </div>
      }
    >
      <div className={clsx(variantClass, styles.imageWrap)}>
        <img className={styles.heroImage} src={imgSrc} alt="" />
      </div>
      <div className={styles.contentArea}>{children}</div>
    </FixedHeaderScroller>
  );
}
